import { useState } from 'react';
import { FormStep } from 'remote/global';
import { CorporateCharterRequest } from 'utils/data/data-types';
import {
  CorporateCharterItinerarySchemaType,
  CorporateCharterScheduleSchemaType,
  LineDetailsSchemaType,
  CorporateCharterClientDetailsSchemaType,
} from 'components/forms/CorporateCharter/types';
import { toast } from 'react-hot-toast';
import { isEqual } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { usePostCorporateChartersItineraryCalculatorMutation } from 'api/queries/corporate-charters';
import { usePostOperatorsCorporateChartersMutation } from 'api/queries/operators';
import CreateItinerary from 'components/modals/AddRegularTripModal/CreateItinerary';
import ScheduleOverview from 'components/modals/AddRegularTripModal/ScheduleOverview';
import LineDetailsForm from 'components/modals/AddRegularTripModal/LineDetailsForm';
import ClientDetailsForm from 'components/modals/AddRegularTripModal/ClientDetailsForm';

type FormResult = [
  CorporateCharterItinerarySchemaType,
  CorporateCharterScheduleSchemaType,
  LineDetailsSchemaType,
  CorporateCharterClientDetailsSchemaType
];

interface Props {
  onSubmit: () => void;
  activeStep: number;
  setActiveStep: (step: number) => void;
}

function CreateCharterRideForm({ onSubmit, activeStep, setActiveStep }: Props) {
  const { t } = useTranslation();
  const [formResult, setFormResult] = useState<FormResult>();
  const [itineraryValues, scheduleValues, lineDetailValues, clientDetailValues] = formResult ?? [];
  const postCorporateChartersItineraryCalculatorMutation =
    usePostCorporateChartersItineraryCalculatorMutation();
  const postOperatorsCorporateChartersMutation = usePostOperatorsCorporateChartersMutation();

  const steps: FormStep[] = [
    {
      label: t('CorporateCharterView.steps.itinerary.title'),
      description: '',
    },
    {
      label: t('CorporateCharterView.steps.schedule.title'),
      description: '',
    },
    {
      label: t('CorporateCharterView.steps.lineDetails.title'),
      description: '',
    },
    {
      label: t('CorporateCharterView.steps.clientDetails.title'),
      description: '',
    },
  ];

  const setFormResultForStep = (step: number, data: any) => {
    setFormResult((prevState: any) => {
      const newState = prevState ?? [];
      newState[step] = data;
      return newState;
    });
  };

  const handleGoForward = (data: any) => {
    const nextStepIndex = activeStep + 1;
    // If is not the last step
    if (nextStepIndex < steps.length) {
      setFormResultForStep(activeStep, data);
      setActiveStep(nextStepIndex);
    }
  };

  const handleGoBack = (data: any) => {
    setFormResultForStep(activeStep, data);
    setActiveStep(activeStep - 1);
  };

  const handleValidStep = async (data: any) => {
    const nextStepIndex = activeStep + 1;
    switch (nextStepIndex) {
      case 1: {
        const isItineraryEqualToData = isEqual(data, itineraryValues);
        if (isItineraryEqualToData) {
          handleGoForward(data);
          return;
        }
        try {
          const itineraryResponse =
            await postCorporateChartersItineraryCalculatorMutation.mutateAsync(data);
          if (itineraryResponse) {
            const newItineraryResponse = {
              ...itineraryResponse,
              stops: itineraryResponse.stops.map((stop, index) => {
                return {
                  ...stop,
                  name:
                    stop.location.placeId === scheduleValues?.stops[index]?.location.placeId
                      ? scheduleValues?.stops[index].name
                      : '',
                };
              }),
            };

            setFormResultForStep(nextStepIndex, newItineraryResponse);
            handleGoForward(data);
          } else {
            toast.success(t('Toast.success'));
          }
        } catch (err) {
          // TODO: Capture sentry error
        }
        break;
      }
      case 4: {
        // Handle submitting the form
        const payload: CorporateCharterRequest = {
          clientId: data.clientId,
          contactInfo: {
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            phone: data.phone,
          },
          departures: lineDetailValues!.departures,
          encodedPolylines: scheduleValues!.encodedPolylines,
          lineName: lineDetailValues!.lineName,
          passengerCount: itineraryValues!.passengerCount,
          stops: scheduleValues!.stops,
          ridePriceExclVat: lineDetailValues!.ridePriceExclVat,
        };

        try {
          await postOperatorsCorporateChartersMutation.mutateAsync(payload);
          toast.success(t('Toast.success'));
          onSubmit();
        } catch (err) {
          // TODO: Capture sentry error
        }
        break;
      }
      default: {
        handleGoForward(data);
        break;
      }
    }
  };

  return (
    <>
      {activeStep === 0 && (
        <CreateItinerary defaultValues={itineraryValues} onValid={handleValidStep} />
      )}
      {activeStep === 1 && (
        <ScheduleOverview
          onValid={handleValidStep}
          defaultValues={scheduleValues!}
          onBack={handleGoBack}
        />
      )}
      {activeStep === 2 && (
        <LineDetailsForm
          onValid={handleValidStep}
          itineraryValues={itineraryValues!}
          scheduleValues={scheduleValues!}
          defaultValues={lineDetailValues}
          onBack={handleGoBack}
        />
      )}
      {activeStep === 3 && (
        <ClientDetailsForm
          defaultValues={clientDetailValues}
          onValid={handleValidStep}
          onBack={handleGoBack}
        />
      )}
    </>
  );
}

export default CreateCharterRideForm;
